import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DOCUMENTS_PER_PAGE_DEFAULT, PAGE_DEFAULT, Paginable, SearchConfig, SearchService } from './search.service';

interface SearchResponse<T> {
  found: number;
  hits?: {
    document: T;
  }[];
  page: number;
}

@Injectable()
export class SearchServiceApi implements SearchService {
  private baseUrl: string;
  private headers: HttpHeaders;

  constructor(
    private http: HttpClient,
  ) {
    const { apiKey, url } = environment.typesenseConfig;
    this.baseUrl = url;
    this.headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-TYPESENSE-API-KEY': apiKey,
    });
  }

  async get<T>(collection: string, id: string): Promise<T | null> {
    try {
      return await firstValueFrom(this.http.get<T>(`${this.baseUrl}/collections/${collection}/documents/${id}`, { headers: this.headers }));
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async search<T>(collection: string, config?: SearchConfig): Promise<Paginable<T>> {
    let params: HttpParams = new HttpParams();
    if (config?.query?.value) {
      const { fields, value: q, weights } = config.query;
      params = params.set('q', q);
      params = params.set('query_by', fields.join(','));
      params = params.set('query_by_weights', weights.join(','));
    } else {
      params = params.set('q', '*');
    }
    if (config?.order) {
      const { field, direction } = config.order;
      params = params.set('sort_by', [`${field}:${direction ?? 'desc'}`, '_text_match:desc'].join(','));
    } else {
      params = params.set('sort_by', '_text_match:desc');
    }
    if (config?.filters) {
      const { filters } = config;
      let filterBy = '';
      filters.forEach((filter, index) => {
        if (filter.operator === 'in') {
          filterBy = `${filterBy}${filter.field}:=[${(filter.value as string[]).toString()}]`;
        } else {
          filterBy = `${filterBy}${filter.field}:${filter.operator}${filter.value.toString()}`;
        }
        if (index < filters.length - 1) {
          filterBy = `${filterBy} && `;
        }
      });
      params = params.set('filter_by', filterBy);
    }
    const documentsPerPage = config?.pagination?.documentsPerPage ?? DOCUMENTS_PER_PAGE_DEFAULT;
    const page = config?.pagination?.page ?? PAGE_DEFAULT;
    params = params.set('per_page', documentsPerPage.toString());
    params = params.set('page', page.toString());
    const searchUrl = `${this.baseUrl}/collections/${collection}/documents/search`;
    const search = await firstValueFrom(this.http.get<SearchResponse<T>>(searchUrl, { headers: this.headers, params }));
    const totalItems = search.found || 0;
    const done = totalItems > 0 ? Math.ceil((totalItems / documentsPerPage)) === page : true;
    return {
      data: (search.hits ?? []).map((hit) => hit.document),
      done,
      getMore: () => this.search<T>(collection, {
        filters: config?.filters,
        query: {
          fields: config?.query?.fields ?? [],
          value: config?.query?.value ?? null,
          weights: config?.query?.weights ?? [],
        },
        order: config?.order,
        pagination: {
          documentsPerPage,
          page: page + 1,
        },
      }),
      size: documentsPerPage,
      totalItems,
    };
  }
}